<template>
  <div
    v-if="modelValue"
    class="cancellation-summary-modal"
  >
    <div
      class="cancellation-summary-modal__overlay"
      @click.self="handleClose"
    >
      <div class="cancellation-summary-modal__content">
        <div class="cancellation-summary-modal__header">
          <h2 class="cancellation-summary-modal__title">
            {{ $t('afterSale.cancellation.confirmTitle') }}
          </h2>
          <button
            :aria-label="$t('afterSale.cancellation.close')"
            class="cancellation-summary-modal__close"
            @click="handleClose"
          >
            <svg>
              <use :href="cancel" />
            </svg>
          </button>
        </div>

        <div class="cancellation-summary-modal__body">
          <div
            v-if="selectedSegments"
            class="cancellation-summary-modal__content-wrapper"
          >
            <div class="cancellation-summary-modal__section">
              <h3>{{ $t('afterSale.cancellation.reviewChoices') }}</h3>
              <AfterSalesTravel
                v-for="segment in selectedSegments"
                :key="segment.id"
                :segment="segment"
                :direction-label="
                  segment.direction === 'outbound'
                    ? $t('afterSale.cancellation.directions.outbound')
                    : $t('afterSale.cancellation.directions.return')
                "
                :show-checkbox="false"
                :selected-passengers="selectedPassengers"
                :passengers="passengers"
                :has-passengers="true"
                class="cancellation-summary-modal__travel"
              />
            </div>

            <div class="cancellation-summary-modal__section">
              <div
                class="cancellation-summary-modal__price-row cancellation-summary-modal__total"
              >
                <span>{{ $t('afterSale.cancellation.refundAmount') }}:</span>
                <span>
                  {{ formatPrice(refundAmount) }}
                  {{ $t('afterSale.cancellation.currency') }}
                </span>
              </div>
              <p>{{ $t('afterSale.cancellation.refundText') }}</p>
            </div>
          </div>
        </div>

        <div class="cancellation-summary-modal__footer">
          <button
            class="button button--medium button--secondary"
            @click="handleClose"
          >
            {{ $t('afterSale.cancellation.cancel') }}
          </button>
          <button
            class="button button--medium button--primary"
            :disabled="isProcessing"
            @click="handleConfirm"
          >
            {{
              isProcessing
                ? $t('afterSale.cancellation.processing')
                : $t('afterSale.cancellation.confirm')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onBeforeUnmount, watch, ref } from 'vue'
  import AfterSalesTravel from './AfterSalesTravel.vue'
  import cancel from '@/assets/icons/cancel.svg'
  import { useErrorStore } from '@/stores/errorStore'
  import { BookingTariffSegment } from '@/types/bookingTypes'
  import { Passenger } from '@/types/passengerTypes'

  interface Segment extends BookingTariffSegment {
    direction: 'outbound' | 'inbound'
  }

  interface Props {
    modelValue: boolean
    allSegments: Segment[]
    selectedPassengers: Record<string, boolean>
    passengers: Passenger[]
    totalPrice: number
    cancellationFee: number
    bookingNumber: string
    cancelResponse: any
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'confirm': []
    'close': []
  }>()

  const errorStore = useErrorStore()
  const isProcessing = ref(false)

  const formatPrice = (price: number) => {
    return price.toLocaleString('nb-NO')
  }

  const refundAmount = computed(() => {
    return props.totalPrice - props.cancellationFee
  })

  const handleClose = () => {
    emit('update:modelValue', false)
    emit('close')
  }

  const handleConfirm = async () => {
    try {
      isProcessing.value = true
      await emit('confirm')
      handleClose()
    } catch (error) {
      console.error('Error during confirmation:', error)
      errorStore.setError(String(error))
    } finally {
      isProcessing.value = false
    }
  }

  onMounted(() => {
    if (props.modelValue) {
      document.body.classList.add('no-scroll')
    }
  })

  onBeforeUnmount(() => {
    document.body.classList.remove('no-scroll')
  })

  watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    }
  )

  const selectedSegments = computed(() => {
    return props.allSegments.filter((segment) => {
      return segment.required_products?.some(
        (product) => props.selectedPassengers[product.uuid]
      )
    })
  })
</script>

<style lang="scss" scoped>
  .cancellation-summary-modal {
    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(42, 52, 122, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      overflow-y: auto;
      padding: 16px;
      z-index: 20;
    }

    &__content {
      background-color: white;
      position: relative;
      min-width: 360px;
      max-width: 90vw;
      margin: auto;
      display: flex;
      flex-direction: column;
      border-radius: 4px;
      padding: 24px;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    &__title {
      font-size: 24px;
      font-weight: 600;
      margin: 0;
    }

    &__close {
      width: 32px;
      height: 32px;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
      color: black;

      svg {
        width: 24px;
        height: 24px;
      }
    }

    &__section {
      margin-bottom: 24px;

      h3 {
        margin: 0 0 16px;
        font-size: 18px;
        font-weight: 600;
      }
    }

    &__price-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    &__total {
      font-weight: 600;
      font-size: 18px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #e5e5e5;
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      margin-top: 24px;
    }

    &__travel {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__travels {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__refund-section {
      margin-top: 24px;
    }

    &__refund-options {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .radio-option {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
</style>
